/**
 * CompanyStatusBadge — visualisation status entreprise (active / archived).
 *
 * Pattern UX commun établi 9.P.2.R1 — réutilisable R2 (Brokers/Guarantees) +
 * R3 (Settings) avec adaptations status-specific.
 *
 * Variantes :
 *   - "active"   → emerald (présence positive, encore en service)
 *   - "archived" → slate (état neutre, hors service)
 *
 * a11y :
 *   - texte i18n bilingue
 *   - `aria-label` optionnel pour contexte additionnel (ex: "Statut entreprise")
 *   - dot pulse `motion-safe:animate-pulse` désactivé via `prefers-reduced-motion`
 */

import { useTranslation } from "react-i18next";

import { cn } from "../../../lib/cn";

type Status = "active" | "archived";

const STATUS_STYLES: Record<Status, { bg: string; text: string; dot: string }> = {
  active: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  archived: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    dot: "bg-slate-400",
  },
};

type Props = {
  readonly status: Status;
  readonly className?: string;
  /** Affiche le dot indicateur (default true). False pour usage compact. */
  readonly showDot?: boolean;
  /** Anime le dot quand status="active" (subtil pulse). Default false. */
  readonly pulseActive?: boolean;
};

export function CompanyStatusBadge({
  status,
  className,
  showDot = true,
  pulseActive = false,
}: Props) {
  const { t } = useTranslation();
  const styles = STATUS_STYLES[status];
  const label =
    status === "active"
      ? t("companies.filters.active")
      : t("companies.filters.archived");

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
        styles.bg,
        styles.text,
        className,
      )}
    >
      {showDot && (
        <span
          aria-hidden="true"
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            styles.dot,
            pulseActive && status === "active"
              ? "motion-safe:animate-pulse motion-reduce:animate-none"
              : "",
          )}
        />
      )}
      {label}
    </span>
  );
}
